import { ButtonCus, ButtonCusWithIcon } from '@/components/common/Button';
import { DISPPLAY_MOMENT_YMD_HM, ekDomain, ROUTES } from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import { OPTION_FORMAT, ORDER_STATUS, ROLE_OWNER } from '@/types/enums';
import { ReservationCustomerInfor, ReservationDateAndTime, ReservationDetail, ReservationDetailItem, ReservationInformation, ReservationServiceItem, ReservationServices } from '@/types/reservation';
import { formatDateTime } from '@/utils/datetime';
import { isPathEk } from '@/utils/routes';
import { getAvatarFallbackName, isRoles } from '@/utils/utils';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LocationIcon } from '@/components/icon';
import { MailIcon } from '@/components/icon/Mail';
import { PhoneIcon } from '@/components/icon/Phone';
import Avatar from '@/components/common/Avatar';
import { formatCurrency, formatToCurrency } from '@/utils/formatNumber';
import UpdateOrderStatus from '../modal/UpdateOrderStatus';

import './style.scss';
import ScrollTableGuide from '@/components/general/ScrollTableGuide';

interface BookingInforProps {
    // infor: ReservationInformation,
    // dateAndTime: ReservationDateAndTime,
    // customerInfor: ReservationCustomerInfor,
    // services?: {
    //     option: ReservationServiceItem[],
    //     compensation: ReservationServiceItem[],
    //     dropOff: ReservationServiceItem[]
    // },
    reservation: ReservationDetailItem,
    isRevenueDetail?: boolean,
    isForEND?: boolean
}

export default observer(function BookingInfor({ reservation, isForEND = false }: BookingInforProps) {
    // hooks
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const isEk = isPathEk(pathname);
    const isOwner = isRoles([...ROLE_OWNER]);

    // store
    const { modalStore: { showModal }, reservationStore: { getPaymentHistory, getReservationCMSDetail } } = useStore();

    const { information: infor = {} as ReservationInformation, renter: customerInfor = {} as ReservationCustomerInfor, details: { dateAndTime = {} as ReservationDateAndTime, services = {} as ReservationServices } = {} as ReservationDetail } = reservation || {};
    const { bike, location } = infor;
    const { rentalFrom, rentalTo, totalAmount } = dateAndTime;
    const { id: customerId, fullname, email, phone, avartarImage = '' } = customerInfor;

    // state
    const scrollRef = useRef<HTMLDivElement>(null);
    const [showHint, setShowHint] = useState<boolean>(false);
    const onFirstScroll = useRef<boolean>(true);

    // lifecycle
    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;

        const checkScrollable = () => setShowHint(el.scrollWidth > el.clientWidth);
        requestAnimationFrame(checkScrollable);

        const handleScroll = () => {
            if (el.scrollLeft > 0 && onFirstScroll.current) {
                onFirstScroll.current = false;
                setShowHint(false);
            }
        };
        el.addEventListener('scroll', handleScroll);
        return () => {
            el.removeEventListener('scroll', handleScroll);
        };
    }, [services]);

    const additionalServices: ReservationServiceItem[] = useMemo(() => {
        let returnServices: ReservationServiceItem[] = [];
        const { option = [], compensation = [], dropOff = [], extraMileage = [] } = services;

        returnServices = [
            ...option.map(item => ({ ...item, format: OPTION_FORMAT.Option })),
            ...compensation.map(item => ({ ...item, format: OPTION_FORMAT.Insurance })),
            ...extraMileage.map(item => ({ ...item, format: OPTION_FORMAT.ExtraMileage })),
            ...dropOff.map(item => ({ ...item, format: OPTION_FORMAT.DropOff }))
        ];
        return returnServices;
    }, [services]);

    // function

    const handleGoToReservationEdit = (id: number) => {
        navigate(isEk ?
            `/${ekDomain}${ROUTES.reservationManagement.href}/${ROUTES.reservationEdit.href}/${id}` :
            `${ROUTES.reservationManagement.href}/${ROUTES.reservationEdit.href}/${id}`, {
            state: {
                previousUrl: pathname
            }
        });
    };

    const onClickUpdateStatusdOrder = (id: number) => {
        showModal({
            id: 'update-reservation-status',
            // title: tAll('words_title.reservation_update'),
            title: t('button.update_reservation_status'),
            size: 'xl',
            titleClassName: 'text-center max-w-none',
            content: (
                <UpdateOrderStatus
                    id={id}
                    reload={() => {
                        getReservationCMSDetail({ id });
                        getPaymentHistory({ id });
                    }}
                />
            ),
            footerClassName: 'hidden'
        });
    };

    return (
        <div className='flex flex-col w-full gap-4 p-4 max-w-full'>
            {/* <h1 className='text-[1.125rem] text-black-1 font-semibold md:text-2xl'>{t('words_title.booking_information')}</h1> */}
            <div className='flex border-[#F5F5F5] border-b justify-between w-full lg:gap-6 xsm:gap-4 gap-1 items-center point-600:pb-4 p-2 rounded-base point-600:bg-background-3 bg-background-6'>
                <div className='flex flex-col text-black-1 point-600:gap-1 gap-2 xsm:min-w-[185px]'>
                    <label className='text-fs-mid14'>{t('field.reservation.rental_time')}</label>
                    <div className='point-375:text-fs-mid16 text-[0.875rem] font-medium xsm:min-w-32'>{rentalFrom && formatDateTime(rentalFrom, DISPPLAY_MOMENT_YMD_HM, { useLocalTime: false })}</div>
                </div>
                <div className='flex flex-1 justify-center items-center'>
                    <div className='border-[#D9D9D9] border-b border-dashed h-[30px] w-full'></div>
                </div>
                <div className='flex flex-col text-black-1 point-600:gap-1 gap-2 items-end xsm:min-w-[185px]'>
                    <label className='text-fs-mid14'>{t('field.reservation.return_time')}</label>
                    <div className='point-375:text-fs-mid16 text-[0.875rem] text-right font-medium xsm:min-w-32'>{rentalTo && formatDateTime(rentalTo, DISPPLAY_MOMENT_YMD_HM, { useLocalTime: false })}</div>
                </div>
            </div>
            <div className='flex sm:justify-between sm:flex-row flex-col sm:gap-3 border-[#F5F5F5] border-b'>
                <div className='flex gap-4 items-center pb-4'>
                    <div className='max-w-[100px] h-[80px]'>
                        {
                            (bike?.vehicleImageRight || bike?.vehicleImageLeft || bike?.vehicleImageBefore || bike?.vehicleImageAfter) && (
                                <img className='h-[80px] max-w-[100px] object-contain' src={bike?.vehicleImageRight || bike?.vehicleImageLeft || bike?.vehicleImageBefore || bike?.vehicleImageAfter || ''} alt='bikeimage' />
                            )
                        }
                    </div>
                    <div className='flex flex-col gap-2'>
                        <p className='xsm:text-[1.0625rem] text-base font-semibold break-all'>
                            <Link
                                to={
                                    !isForEND ?
                                        (isPathEk(pathname) ?
                                            `/${ekDomain}${ROUTES.bikeManagement.href}/${ROUTES.bikeEdit.href}/${bike?.id}` :
                                            `${ROUTES.bikeManagement.href}/${ROUTES.bikeEdit.href}/${bike?.id}`) :
                                        (
                                            isPathEk(pathname) ?
                                                `/${ekDomain}/${ROUTES.locationList.hrefEK}/${ROUTES.locationDetailEnduser.hrefEK}/${location?.id}/${ROUTES.bikeBookingDetail.hrefEK}/${bike?.id}` :
                                                `/${ROUTES.locationList.href}/${ROUTES.locationDetailEnduser.href}/${location?.id}/${ROUTES.bikeBookingDetail.href}/${bike?.id}`)

                                }
                                className='text-[#3B7CEC] hover:underline'
                            >
                                {bike?.bikeModel}
                            </Link>
                        </p>
                        <div className='flex gap-2 items-center'>
                            <div className='szie-4'>
                                <LocationIcon className='fill-[#5C5858] size-4' />
                            </div>
                            <p className='text-[#5C5858] text-[0.8125rem] break-all'>
                                {
                                    !isOwner ?
                                        (
                                            <Link
                                                to={
                                                    !isForEND ?
                                                        (isPathEk(pathname) ?
                                                            `/${ekDomain}${ROUTES.locationManagement.href}/${ROUTES.locationEdit.href}/${location?.id}` :
                                                            `${ROUTES.locationManagement.href}/${ROUTES.locationEdit.href}/${location?.id}`) :
                                                        (
                                                            isPathEk(pathname) ?
                                                                `/${ekDomain}/${ROUTES.locationList.hrefEK}/${ROUTES.locationDetailEnduser.hrefEK}/${location?.id}` :
                                                                `/${ROUTES.locationList.href}/${ROUTES.locationDetailEnduser.href}/${location?.id}`)

                                                }
                                                className='hover:underline'
                                            >
                                                {location?.name}
                                            </Link>
                                        ) :
                                        location?.name
                                }
                            </p>
                        </div>
                    </div>
                </div>
                <div className='flex gap-1 items-center text-fs-mid18 font-semibold sm:w-fit w-full sm:justify-start justify-between sm:pb-4 p-3 rounded-base sm:bg-background-3 bg-background-6'>
                    <label className='text-[#6F767E] !w-[50px]'>
                        <span>{t('field.fee')}</span>
                        :
                    </label>
                    <p className='text-primaryWeb'>
                        {totalAmount && t('currency.currency_symbol')}
                        {formatToCurrency(totalAmount)}
                    </p>
                </div>
            </div>

            {
                additionalServices && additionalServices.length > 0 && (
                    <div className='border-[#F5F5F5] border-b pb-4 '>
                        <p className='text-[#333333] text-fs-mid18 font-semibold'>{t('field.select_option')}</p>
                        <div className='mt-2 xsm:overflow-hidden overflow-x-auto relative' ref={scrollRef}>
                            <ScrollTableGuide className='xsm:hidden' showHint={showHint && onFirstScroll.current} />
                            <table className='border border-collapse text-center w-full option-table booking-table'>
                                <thead>
                                    <tr className='bg-gray-5 text-black'>
                                        <th className='border option-item_name w-1/2 p-2'>{t('field.option_page.option_name')}</th>
                                        <th className='border w-[15%] p-2'>
                                            <span
                                                className='unit-price'
                                                data-symbol={t('currency.currency_symbol')}
                                            >
                                                {t('field.unit_price')}
                                            </span>
                                        </th>
                                        <th className='border option-item_quantity w-[10%] p-2'>{t('field.quantity')}</th>
                                        <th className='border option-item_quantity w-[10%] p-2'>{t('field.number_of_weeks')}</th>
                                        <th className='border option-item_price w-[15%] p-2'>
                                            <span
                                                className='unit-price'
                                                data-symbol={t('currency.currency_symbol')}
                                            >
                                                {t('field.fee')}
                                            </span>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {additionalServices.map((item) => {
                                        let unitPrice: number | null = null;

                                        let applyNumberOfWeek = [OPTION_FORMAT.Option.valueOf(), OPTION_FORMAT.Insurance.valueOf()].includes(Number(item.format));

                                        if (formatCurrency(item.fee)) {
                                            unitPrice = Number(item.fee) / ((Number(item.quantity || 1)) * (applyNumberOfWeek ? Number(dateAndTime?.numberOfWeeks || 1) : 1));
                                        }
                                        return (
                                            <tr key={item.id} className='border text-left'>
                                                <td className='border p-2'>{item.name}</td>
                                                <td className='border p-2 text-right'>{formatCurrency(unitPrice)}</td>
                                                <td className='border p-2 text-center'>{item.quantity}</td>
                                                <td className='border p-2 text-center'>{applyNumberOfWeek ? dateAndTime?.numberOfWeeks : null}</td>
                                                <td className='border p-2 text-right text-customRed font-medium'>{formatCurrency(item.fee)}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )
            }

            {
                (!isOwner && !isForEND) && (
                    <div className='flex gap-4 items-center'>
                        <div>
                            {
                                avartarImage ?
                                    <img className='h-[88px] max-w-[100px] object-contain' src={avartarImage || ''} alt='avartarimage' /> :
                                    (
                                        <Avatar
                                            src=''
                                            fallback={getAvatarFallbackName(fullname)}
                                            className='border border-primary rounded-full text-4xl size-[88px]'
                                        />
                                    )
                            }

                        </div>
                        <div className='flex flex-col'>
                            <p className='text-[1.0625rem] font-semibold break-all'>
                                <Link
                                    to={
                                        isEk ?
                                            `/${ekDomain}${ROUTES.userManagement.href}/${ROUTES.userEdit.href}/${customerId}` :
                                            `${ROUTES.userManagement.href}/${ROUTES.userEdit.href}/${customerId}`
                                    }
                                    className='text-[#3B7CEC] hover:underline'
                                >
                                    {fullname}
                                </Link>
                            </p>
                            <div className='flex gap-2 items-center'>
                                <div className='size-4'>
                                    <MailIcon width={16} height={16} />
                                </div>

                                <p className='text-[#5C5858] text-[0.8125rem] break-all'>
                                    {email && (<a className='cursor-pointer hover:underline' href={`mailto:${email}`}>{email}</a>)}
                                </p>
                            </div>
                            <div className='flex gap-2 items-center'>
                                <div className='size-4'>
                                    <PhoneIcon width={16} height={16} />
                                </div>
                                <p className='text-[#5C5858] text-[0.8125rem]'>
                                    {phone && (<a className='cursor-pointer hover:underline' href={`tel:${phone}`}>{phone}</a>)}
                                </p>
                            </div>
                        </div>
                    </div>
                )
            }
            {
                !isForEND && !isOwner && (
                    <div className='flex gap-4 justify-center mt-2'>
                        {
                            [ORDER_STATUS.Add_Not_Paid.valueOf(), ORDER_STATUS.Not_Paid.valueOf(), ORDER_STATUS.Paid.valueOf()].includes(reservation?.information?.orderStatus) && (
                                <ButtonCusWithIcon
                                    onClick={() => handleGoToReservationEdit(reservation?.information?.id)}
                                    size='md'
                                    // variant='green'
                                    variant='default'
                                    iconId='#icon-pen-book'
                                    widthIcon={20}
                                    heightIcon={18}
                                >
                                    {t('button.update_reservation')}
                                </ButtonCusWithIcon>
                            )
                        }
                        {
                            ![ORDER_STATUS.Cancel.valueOf(), ORDER_STATUS.Return.valueOf()].includes(reservation?.information?.orderStatus) && (
                                <ButtonCus
                                    variant='default'
                                    size='md'
                                    type='button'
                                    onClick={() => {
                                        onClickUpdateStatusdOrder(reservation?.information?.id);
                                    }}
                                >
                                    {t('button.update_reservation_status')}
                                </ButtonCus>
                            )
                        }
                    </div>
                )
            }

        </div>
    );
});
